import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  PrimaryPhysicianInfoRec,
  primaryPhysicianInfoService,
} from '../../../../services/primaryPhysicianInfo';
import { tasksService } from '../../../../services/tasks';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('PrimaryPhysicianData');

type RouteParams = {
  primaryPhysicianId?: string;
  targetUserId?: string;
  viewOnly?: boolean;
};

type PrimaryPhysicianDataScreenRouteProp = RouteProp<RootStackParamList, 'PrimaryPhysicianData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | number | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else if (typeof value === 'number') {
    displayValue = value.toString();
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

type SecureInfoItemProps = {
  label: string;
  value: string | undefined;
};

const SecureInfoItem: React.FC<SecureInfoItemProps> = ({ label, value }) => {
  const [isVisible, setIsVisible] = useState(false);

  const displayValue = value || 'Not specified';

  return (
    <View style={styles.infoItem}>
      <View style={styles.secureRow}>
        <Text style={styles.label}>{label} 🔒</Text>
        <TouchableOpacity onPress={() => setIsVisible(!isVisible)} style={styles.eyeButton}>
          <Image
            source={isVisible
              ? require('../../../../assets/icons/Eye.png')
              : require('../../../../assets/icons/Eye-closed.png')}
            style={styles.eyeIcon}
            resizeMode="contain"
          />
        </TouchableOpacity>
      </View>
      <Text style={styles.value}>
        {isVisible ? displayValue : '••••••••'}
      </Text>
      <View style={styles.separator} />
    </View>
  );
};

export const PrimaryPhysicianDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const [primaryPhysicianInfo, setPrimaryPhysicianInfo] = useState<PrimaryPhysicianInfoRec | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalCredentials, setPortalCredentials] = useState<{ username?: string; password?: string } | null>(null);

  const primaryPhysicianId = route.params?.primaryPhysicianId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  useEffect(() => {
    loadPrimaryPhysicianInfo();
  }, [targetUserId]);

  const loadPrimaryPhysicianInfo = async () => {
    try {
      setLoading(true);
      const response = await primaryPhysicianInfoService.getAllPrimaryPhysicianInfo(targetUserId);
      logger.debug('[Primary Physician Debug] Loaded all primary physician entries:', response);

      // Handle response format - check if it's wrapped in a success/data object
      const data = response?.data || response;
      logger.debug('[Primary Physician Debug] Extracted data array:', data);
      logger.debug('[Primary Physician Debug] Data length:', data?.length || 0);

      let selectedInfo = null;
      if (data && Array.isArray(data) && data.length > 0) {
        if (primaryPhysicianId) {
          // If we have a specific ID, find that record
          const info = data.find(item => item.id === primaryPhysicianId);
          logger.debug('[Primary Physician Debug] Found specific primary physician info:', info);
          selectedInfo = info || null;
        } else {
          // Set the first/latest entry
          logger.debug('[Primary Physician Debug] Setting first primary physician data entry:', data[0]);
          selectedInfo = data[0];
        }
        setPrimaryPhysicianInfo(selectedInfo);

        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          // Load portal credentials from vault if we have an ID
          if (selectedInfo?.id) {
            try {
              const credentials = await vaultService.getPortalCredentials(selectedInfo.id, 'primary_physician');
              logger.debug('[Primary Physician Debug] Loaded portal credentials from vault');
              setPortalCredentials(credentials);
            } catch (error) {
              logger.debug('[Primary Physician Debug] Failed to load portal credentials:', error);
              setPortalCredentials(null);
            }
          }
        }
      } else {
        logger.debug('[Primary Physician Debug] No primary physician entries found or invalid data format');
        setPrimaryPhysicianInfo(null);
      }
    } catch (error) {
      logger.error('Error loading primary physician data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (primaryPhysicianInfo) {
      // Map the data from primaryPhysicianInfo to the format expected by PrimaryPhysicianInputScreen
      const mappedData = {
        id: primaryPhysicianInfo.id,
        physicianName: primaryPhysicianInfo.physicianName,
        practiceName: primaryPhysicianInfo.practiceName,
        phoneNumber: primaryPhysicianInfo.phoneNumber,
        faxNumber: primaryPhysicianInfo.faxNumber,
        email: primaryPhysicianInfo.email,
        address: primaryPhysicianInfo.address,
        city: primaryPhysicianInfo.city,
        state: primaryPhysicianInfo.state,
        zipCode: primaryPhysicianInfo.zipCode,
        yearsAsPatient: primaryPhysicianInfo.yearsAsPatient,
        lastVisitDate: primaryPhysicianInfo.lastVisitDate,
        nextAppointmentDate: primaryPhysicianInfo.nextAppointmentDate,
        medicalRecordNumber: primaryPhysicianInfo.medicalRecordNumber,
        insuranceAccepted: primaryPhysicianInfo.insuranceAccepted,
        referralRequired: primaryPhysicianInfo.referralRequired,
        emergencyContact: primaryPhysicianInfo.emergencyContact,
        portalUsername: primaryPhysicianInfo.portalUsername,
        portalPassword: primaryPhysicianInfo.portalPassword,
        portalUrl: primaryPhysicianInfo.portalUrl,
        notes: primaryPhysicianInfo.notes,
      };

      navigation.navigate('PrimaryPhysicianInput', {
        primaryPhysicianData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('PrimaryPhysicianInput');
    }
  };

  const handleDelete = () => {
    if (primaryPhysicianInfo) {
      Alert.alert(
        'Delete Primary Physician Information',
        'Are you sure you want to delete this primary physician information? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await primaryPhysicianInfoService.deletePrimaryPhysicianInfo(primaryPhysicianInfo.id);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await primaryPhysicianInfoService.getAllPrimaryPhysicianInfo();
                const actualData = remainingData?.data || remainingData;
                if (!actualData || actualData.length === 0) {
                  await tasksService.resetTask('primary_physician_info');
                }

                navigation.navigate('MedicalChecklist');
              } catch (error) {
                logger.error('Error deleting primary physician info:', error);
                Alert.alert('Error', 'Failed to delete primary physician information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('MedicalChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Primary Physician Information Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Primary Physician Information Details</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {primaryPhysicianInfo ? (
            <>
              {/* Display all relevant fields using InfoItem components */}
              {primaryPhysicianInfo.physicianName && (
                <InfoItem label="Physician Name" value={primaryPhysicianInfo.physicianName} />
              )}
              {primaryPhysicianInfo.practiceName && (
                <InfoItem label="Practice Name" value={primaryPhysicianInfo.practiceName} />
              )}
              {primaryPhysicianInfo.phoneNumber && (
                <InfoItem label="Phone Number" value={primaryPhysicianInfo.phoneNumber} />
              )}
              {primaryPhysicianInfo.faxNumber && (
                <InfoItem label="Fax Number" value={primaryPhysicianInfo.faxNumber} />
              )}
              {primaryPhysicianInfo.email && (
                <InfoItem label="Email" value={primaryPhysicianInfo.email} />
              )}
              {primaryPhysicianInfo.address && (
                <InfoItem label="Address" value={primaryPhysicianInfo.address} />
              )}
              {primaryPhysicianInfo.city && (
                <InfoItem label="City" value={primaryPhysicianInfo.city} />
              )}
              {primaryPhysicianInfo.state && (
                <InfoItem label="State" value={primaryPhysicianInfo.state} />
              )}
              {primaryPhysicianInfo.zipCode && (
                <InfoItem label="Zip Code" value={primaryPhysicianInfo.zipCode} />
              )}
              {primaryPhysicianInfo.yearsAsPatient && (
                <InfoItem label="Years as Patient" value={primaryPhysicianInfo.yearsAsPatient} />
              )}
              {primaryPhysicianInfo.lastVisitDate && (
                <InfoItem label="Last Visit Date" value={primaryPhysicianInfo.lastVisitDate} />
              )}
              {primaryPhysicianInfo.nextAppointmentDate && (
                <InfoItem label="Next Appointment Date" value={primaryPhysicianInfo.nextAppointmentDate} />
              )}
              {primaryPhysicianInfo.medicalRecordNumber && (
                <InfoItem label="Medical Record Number" value={primaryPhysicianInfo.medicalRecordNumber} />
              )}
              {primaryPhysicianInfo.insuranceAccepted && (
                <InfoItem label="Insurance Accepted" value={primaryPhysicianInfo.insuranceAccepted} />
              )}
              {primaryPhysicianInfo.referralRequired !== undefined && (
                <InfoItem label="Referral Required" value={primaryPhysicianInfo.referralRequired} />
              )}
              {primaryPhysicianInfo.emergencyContact !== undefined && (
                <InfoItem label="Emergency Contact" value={primaryPhysicianInfo.emergencyContact} />
              )}
              {primaryPhysicianInfo.portalUrl && (
                <InfoItem label="Patient Portal URL" value={primaryPhysicianInfo.portalUrl} />
              )}
              {portalCredentials?.username && (
                <SecureInfoItem label="Portal Username" value={portalCredentials.username} />
              )}
              {portalCredentials?.password && (
                <SecureInfoItem label="Portal Password" value={portalCredentials.password} />
              )}
              {primaryPhysicianInfo.notes && (
                <InfoItem label="Notes" value={primaryPhysicianInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>
                {viewOnly ? 'This user has not added primary physician information yet' : 'No primary physician information found.'}
              </Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={handleEdit}
                >
                  <Text style={styles.addButtonText}>Add Primary Physician Info</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0', // Teal header background
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF', // White title on teal background
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // White content area
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280', // Gray label text
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F', // Dark blue value text
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0', // Teal separator line
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0', // Teal back button
  },
  deleteButton: {
    backgroundColor: '#FF4444', // Red delete button
  },
  editButton: {
    backgroundColor: '#FF9F5A', // Orange edit button
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  secureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: ms(8),
  },
  eyeButton: {
    padding: ms(5),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});